import { Drawer } from '@mantine/core';
import clsx from 'clsx';
import { mapValues, noop } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { useMeasure } from 'react-use';
import { darkModeRoot } from 'src/app/App.css';
import { FilterToolbarItemId } from 'src/components/Filters';
import { MobileHeaderBar } from 'src/components/mobile/MobileHeader';
import { useAppContext } from 'src/contexts/AppContext';
import { Content, FormatContent } from 'src/contexts/ContentContext';
import { useMobileInventorySortAndFilterContext } from 'src/contexts/MobileSortAndFilterContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { useSiteTimezoneContext } from 'src/contexts/SiteTimezoneContext/SiteTimezoneContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { vars } from 'src/core/themes';
import { globalMobileWrapper } from 'src/core/themes/themeContract.css';
import { Button, Radio, RadioGroup, Stack } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import {
  DateRangePresetName,
  DateRangeRelativePreset,
  formatRelativeDateRange,
  getDateRangeRelativePresets,
  getLastDateRangeRelative,
} from 'src/utils/dateTimeUtils';

import { ActionToAllFilters } from './components/ActionToAllFilters';
import { EventSelectedFilters } from './components/InventorySelectedFilters';
import { InventoryCustomFilter } from './InventoryCustomFilterContent';
import * as styles from './InventoryListingSortingContent.css';

const OutstandingFilterItemIds: FilterToolbarItemId[] = ['purchaseDates'];

export const InventoryEventFilterDrawer = ({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) => {
  const [headerRef, { height }] = useMeasure<HTMLElement>();
  const { isDarkMode } = useSiteTheme();

  const {
    onSaveFilterQuery,
    onResetFilters,
    selectedFilters,
    tempQuery,
    filters,
    canSaveFilter,
    quickFilters,
    onResetCustomFilter,
    onSaveSelectedFilters,
    onFilterRemoved,
  } = useMobileInventorySortAndFilterContext();

  const hasNonOutstandingFilters =
    useMemo(() => {
      const nonOutstandingFilters = mapValues(selectedFilters, (groupFilters) =>
        groupFilters.filter(
          (filter) => !OutstandingFilterItemIds.includes(filter.filterId)
        )
      );
      return Object.values(nonOutstandingFilters).flatMap((filters) => filters);
    }, [selectedFilters]).length > 0;

  const selectedFilterCount = useMemo(
    () => Object.values(selectedFilters).flatMap((filters) => filters).length,
    [selectedFilters]
  );

  return (
    <Drawer.Root
      zIndex={50}
      opened={show}
      onClose={onClose}
      size="100%"
      position="right"
      className={clsx(globalMobileWrapper, {
        [darkModeRoot]: isDarkMode,
      })}
    >
      <Drawer.Overlay />
      <Drawer.Content
        bg={vars.color.backgroundSecondary}
        style={{ overflowY: 'hidden', color: vars.color.textPrimary }}
        className={styles.drawerContent}
      >
        <Drawer.Header
          ref={headerRef}
          bg={vars.color.backgroundSecondary}
          className={styles.drawerHeader}
        >
          <MobileHeaderBar
            onBack={onClose}
            title={
              hasNonOutstandingFilters ? (
                <FormatContent
                  id={FormatContentId.NFiltersSelected}
                  params={selectedFilterCount + ''}
                />
              ) : (
                <Content id={ContentId.FilterEventsBy} />
              )
            }
            actions={
              !hasNonOutstandingFilters && (
                <InventoryCustomFilter
                  quickFilters={quickFilters}
                  onResetCustomFilter={onResetCustomFilter}
                />
              )
            }
          />
        </Drawer.Header>
        <Drawer.Body
          bg={vars.color.backgroundSecondary}
          style={{ height: `calc(100% - ${height + 20}px`, overflow: 'hidden' }}
          className={styles.drawerBody}
        >
          <Stack
            height="full"
            direction="column"
            gap="m"
            className={styles.drawerInnerContent}
          >
            <Stack
              direction="column"
              gap="l"
              style={{ flex: 1, overflowY: 'auto' }}
            >
              {hasNonOutstandingFilters ? (
                <EventSelectedFilters
                  tempQuery={tempQuery}
                  selectedDropdownItems={selectedFilters}
                  filters={filters}
                  onSaveSelectedFilters={onSaveSelectedFilters}
                  onFilterRemoved={onFilterRemoved}
                />
              ) : (
                <>
                  <EventOutstandingFilters />
                  <EventFilterByEventDate />
                  <ActionToAllFilters
                    selectedFilters={selectedFilters}
                    filters={filters}
                    onSaveSelectedFilters={onSaveSelectedFilters}
                  />
                </>
              )}
            </Stack>
            <Stack
              justifyContent="spaceBetween"
              width="full"
              alignItems="center"
            >
              <Button
                style={{ height: '40px' }}
                shape="rect"
                variant="outlineGray"
                onClick={onResetFilters}
              >
                <Content id={ContentId.Clear} />
              </Button>
              <Button
                style={{ height: '40px' }}
                shape="rect"
                disabled={!canSaveFilter}
                onClick={onSaveFilterQuery}
              >
                <Content id={ContentId.Apply} />
              </Button>
            </Stack>
          </Stack>
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
};

type OutstandingFilterItem = {
  id: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  isSelected: boolean;
  onToggleSelect: () => void;
};

const EventOutstandingFilters = () => {
  const { loginContext } = useAppContext();
  const { tempQuery, setTempQuery } = useMobileInventorySortAndFilterContext();

  const last7Days = getLastDateRangeRelative({ days: 7 });

  const { isRecentlyPurchasedSelected, isImPricingSelected } = useMemo(() => {
    const isRecentlyPurchasedSelected = isEqual(
      tempQuery.purchaseDates,
      last7Days
    );
    const isImPricingSelected = isEqual(
      tempQuery.pricerSellerUserId,
      loginContext?.user?.userId
    );
    return {
      isRecentlyPurchasedSelected,
      isImPricingSelected,
    };
  }, [
    last7Days,
    loginContext?.user?.userId,
    tempQuery.pricerSellerUserId,
    tempQuery.purchaseDates,
  ]);

  const onToggleLast7Days = useCallback(() => {
    setTempQuery({
      ...tempQuery,
      purchaseDates: isRecentlyPurchasedSelected ? null : last7Days,
    });
  }, [isRecentlyPurchasedSelected, last7Days, setTempQuery, tempQuery]);

  const onTogglePricerUserId = useCallback(() => {
    setTempQuery({
      ...tempQuery,
      pricerSellerUserId: isImPricingSelected
        ? null
        : (loginContext?.user?.userId ?? null),
    });
  }, [
    isImPricingSelected,
    loginContext?.user?.userId,
    setTempQuery,
    tempQuery,
  ]);

  const outstandingFilters = useMemo(() => {
    const filters: OutstandingFilterItem[] = [];
    filters.push({
      id: 'purchaseDates',
      title: <Content id={ContentId.RecentlyPurchased} />,
      description: (
        <FormatContent id={FormatContentId.LastNDays} params={[`${7}`]} />
      ),
      isSelected: isRecentlyPurchasedSelected,
      onToggleSelect: onToggleLast7Days,
    });
    filters.push({
      id: 'pricerSellerUserId',
      title: <Content id={ContentId.ImPricing} />,
      isSelected: isImPricingSelected,
      onToggleSelect: onTogglePricerUserId,
    });
    return filters;
  }, [
    isImPricingSelected,
    isRecentlyPurchasedSelected,
    onToggleLast7Days,
    onTogglePricerUserId,
  ]);

  return (
    <Stack direction="column" gap="m">
      <span className={styles.subtitleAfter}>
        <Content id={ContentId.FilterBy} />
      </span>
      <Stack direction="column" gap="s">
        {outstandingFilters.map((filter) => {
          return (
            <Stack
              role="button"
              key={filter.id}
              className={styles.inventoryFilterGroupItem}
              alignItems="center"
              justifyContent="spaceBetween"
              onClick={filter.onToggleSelect}
            >
              <Checkbox
                checked={filter.isSelected}
                label={filter.title}
                color={vars.color.textBrand}
                checkboxSizeVariant="light"
                onChange={noop}
                labelPosition="right"
              />
              {filter.description && (
                <span className={styles.sotringOrderDescription}>
                  {filter.description}
                </span>
              )}
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
};

const EventDatePresets: DateRangePresetName[] = [
  DateRangePresetName.Yesterday,
  DateRangePresetName.Today,
  DateRangePresetName.NextWeek,
  DateRangePresetName.Next30Days,
];

const EventFilterByEventDate = () => {
  const presetEventDateFilters = getDateRangeRelativePresets(EventDatePresets);
  const { tempQuery, setTempQuery } = useMobileInventorySortAndFilterContext();

  const onUpdateRelativeDateRange = useCallback(
    (preset: DateRangeRelativePreset) => {
      const { range } = preset;
      setTempQuery({
        ...tempQuery,
        eventDates: range,
      });
    },
    [setTempQuery, tempQuery]
  );

  const selectedPreset = useMemo(() => {
    return presetEventDateFilters.find(({ range }) =>
      isEqual(range, tempQuery.eventDates)
    );
  }, [presetEventDateFilters, tempQuery.eventDates]);

  return (
    <Stack direction="column" gap="m">
      <span className={styles.subtitleAfter}>
        <Content id={ContentId.FilterByEventDate} />
      </span>
      <RadioGroup value={selectedPreset?.name} onValueChange={noop}>
        <Stack direction="column" gap="s">
          {presetEventDateFilters.map((preset) => (
            <InventoryEventDateSelectorItem
              key={preset.name}
              dateRangeRelativePreset={preset}
              onSelect={onUpdateRelativeDateRange}
            />
          ))}
        </Stack>
      </RadioGroup>
    </Stack>
  );
};

const InventoryEventDateSelectorItem = ({
  dateRangeRelativePreset,
  onSelect,
}: {
  dateRangeRelativePreset: DateRangeRelativePreset;
  onSelect: (dateItem: DateRangeRelativePreset) => void;
}) => {
  const { toZonedTime } = useSiteTimezoneContext();

  const onSelectRelativePreset = useCallback(() => {
    onSelect(dateRangeRelativePreset);
  }, [dateRangeRelativePreset, onSelect]);
  return (
    <Stack
      role="button"
      width="full"
      className={styles.inventoryFilterGroupItem}
      style={{ cursor: 'pointer' }}
      justifyContent="spaceBetween"
      onClick={onSelectRelativePreset}
    >
      <Radio
        value={dateRangeRelativePreset.name}
        label={dateRangeRelativePreset.label}
        onChange={onSelectRelativePreset}
      />
      <span className={styles.sotringOrderDescription}>
        {formatRelativeDateRange(dateRangeRelativePreset.range, toZonedTime)}
      </span>
    </Stack>
  );
};
